import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomTextarea from "../../common/CustomTextarea";
import CustomAutocomplete from "../../common/CustomAutoComplete";
import { UPDATE_IMPORT_PAYMENT_VOUCHER, READ_IMPORT_PAYMENT_VOUCHER, DELETE_IMPORT_PAYMENT_VOUCHER_PARTICULAR } from '../../../services/apiRoutes';
import CustomStyle from "../../common/CustomStyle";
import CustomPrefixSuffixField from "../../common/CustomPrefixSuffixField";
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ClearRounded, SaveRounded, AddRounded, } from "@mui/icons-material";
import CustomDelete from '../../common/CustomDeleteAlert';
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    date: Yup.date().required('Date is required'),
    paymentVoucherNo: Yup.string().required('Payment Voucher No is required'),
    amount: Yup.number().required('Amount is required'),
    currencyId: Yup.number().required('Currency is required'),
    baseCurrencyId: Yup.number().required('Base Currency is required'),
    conversionRate: Yup.number().required('Conversion Rate is required'),
    amountInBaseCurrency: Yup.number().required('Amount in Base Currency is required'),
    bankCharges: Yup.number().required('Bank Charges are required'),
    paymentTo: Yup.number().required('Payment To is required'),
    partyLedgerId: Yup.number().when("paymentTo", {
        is: 3,
        then: (schema) => schema.required("Party Ledger is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
    clientCompanyId: Yup.number().when("paymentTo", {
        is: (val) => val === 1 || val === 2,
        then: (schema) => schema.required("Customer Name is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
    // particulars: Yup.array()
    //     .of(
    //         Yup.object().shape({
    //             exportReferenceCiNo: Yup.string().required("CI is required"),
    //             referenceAmount: Yup.number().required("amount is required"),
    //             exportPiNo: Yup.string().required("PI is required"),
    //             amountInBaseCurrency: Yup.number().required("amount In Base is required"),
    //             conversionRate: Yup.number().required("conversion Rate is required"),
    //         })
    //     )
    //     .required("Particulars are required"),

    particulars: Yup.array().when("paymentTo", {
        is: (val) => val === 1 || val === 2,
        then: (schema) =>
            schema
                .of(
                    Yup.object().shape({
                        exportReferenceCiNo: Yup.string().required("CI is required"),
                        referenceAmount: Yup.number().required("amount is required"),
                        exportPiNo: Yup.string().required("PI is required"),
                        // paymentTypeId: Yup.string().required("payment Type is required"),
                        amountInBaseCurrency: Yup.number().required("amount In Base is required"),
                        conversionRate: Yup.number().required("conversion Rate is required"),
                    })
                )
                .min(1, "At least one particular is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
});


const UpdatePayment = ({ handleClose, setMessage, fetchData, commercialInvoiceList, paymentToList, partyLedger, customerList, paymentVoucherId, proformaInvoiceList, currencyList, paymentType }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currencySymbol, setCurrencySymbol] = useState(null);
    const [baseCurrencySymbol, setBaseCurrencySymbol] = useState(null);
    const [deleteAlert, setDeleteAlert] = useState(false)
    const [rowId, setRowId] = useState('')

    const handleOpenDeleteAlert = (rowId) => {
        setDeleteAlert(true)
        setRowId(rowId)
    }

    const fetchDataView = async () => {
        if (!paymentVoucherId) return;
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.get(READ_IMPORT_PAYMENT_VOUCHER(paymentVoucherId), {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const currencySymbol = response?.data?.paymentVoucher?.currency?.currency_symbol || '';
            const baseCurrencySym = response?.data?.paymentVoucher?.baseCurrency?.currency_symbol || '';
            setBaseCurrencySymbol(baseCurrencySym);
            setCurrencySymbol(currencySymbol);
            setData(response.data.paymentVoucher);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDataView();
    }, [paymentVoucherId]);


    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            // Prepare payload
            const payload = { ...values };
            // If paymentTo is 3, remove particulars from payload
            if (values.paymentTo === 3) {
                delete payload.particulars;
            }
            const response = await axios.put(UPDATE_IMPORT_PAYMENT_VOUCHER(paymentVoucherId), payload, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Payment Voucher updated successfully",
                    type: "success",
                });
                fetchData();
                setTimeout(() => {
                    handleClose();
                }, 500);
            } else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (paymentVoucherParticularsId) => {
        setLoading(true);
        const authToken = sessionStorage.getItem('accessToken');
        try {
            const response = await axios.delete(DELETE_IMPORT_PAYMENT_VOUCHER_PARTICULAR(paymentVoucherParticularsId), {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });
            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: "deleted successfully",
                    type: "success",
                });
                fetchDataView();
                setDeleteAlert(false);
            } else {
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            if (error.response) {
                if (error.response.status === 409) {
                    setMessage({
                        open: true,
                        text: error.response.data?.error || "Cannot delete Data. Related Shipment exists.",
                        type: "error",
                    });
                } else {
                    setMessage({
                        open: true,
                        text: "An error occurred while deleting the Data.",
                        type: "error",
                    });
                }
            } else {
                console.error("Error deleting Data", error);
                setMessage({
                    open: true,
                    text: "Network error. Please try again later.",
                    type: "error",
                });
            }
        }
    };

    return (
        <>
            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik
                    initialValues={{
                        date: data?.date || '',
                        paymentVoucherNo: data?.paymentVoucherNo || '',
                        amount: data?.amount || '',
                        currencyId: data?.currencyId || '',
                        baseCurrencyId: data?.baseCurrencyId || '',
                        conversionRate: data?.conversionRate || '',
                        clientCompanyId: data?.clientCompanyId || '',
                        amountInBaseCurrency: data?.amountInBaseCurrency || '',
                        bankCharges: data?.bankCharges || '',
                        note: data?.note || '',
                        reference: data?.reference || '',
                        paymentTo: data?.paymentTo || '',
                        partyLedgerId: data?.paymentTo === 3 ? data?.partyLedgerId : (data?.paymentTo === 1 || data?.paymentTo === 2) ? null : null,
                        particulars: data.particulars.map(particular => ({
                            paymentVoucherParticularsId: particular.paymentVoucherParticularsId,
                            exportReferenceCiNo: parseInt(particular.exportReferenceCiNo,),
                            referenceAmount: parseFloat(particular.referenceAmount).toFixed(2),
                            exportPiNo: parseInt(particular.exportPiNo,),
                            // paymentTypeId: parseInt(particular.paymentTypeId,),
                            paymentTypeId: '1',
                            amountInBaseCurrency: parseFloat(particular.amountInBaseCurrency).toFixed(2),
                            conversionRate: parseFloat(particular.conversionRate).toFixed(2),
                            shipmentOrgId: particular?.shipmentOrgId || '',
                            commanShipmentId: particular?.commanShipmentId || '',
                            paymentVoucherNo: particular?.paymentVoucherNo || '',
                            currencyId: particular?.currencyId || '',
                            baseCurrencyId: particular?.baseCurrencyId || '',
                            userId: particular?.userId || '',
                            companyId: particular?.companyId || '',
                            date: particular?.date || '',
                            bankCharges: particular?.bankCharges || '',
                            importReferenceCiNo: "",
                        }))

                    }}
                    validationSchema={ValidationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                        <Form>
                            <Grid container spacing={1} style={CustomStyle.container}>
                                <Grid item xs={4} md={4}>
                                    <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                        {t("Update Payment Voucher")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                    <Box style={CustomStyle.buttonBox}>
                                        <CustomButton
                                            startIcon={<SaveRounded />}
                                            title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                            size="small"
                                            variant="contained"
                                            type="submit"
                                            disabled={isSubmitting}
                                        />

                                        <CustomButton
                                            startIcon={<ClearRounded />}
                                            title="Cancel"
                                            size="small"
                                            variant="contained"
                                            onClick={handleClose}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                                {/* @@@@@@@@@@@@@@@ start */}

                                <Grid item xs={12} md={6}>
                                    <CustomAutocomplete
                                        options={paymentToList}
                                        fieldLabel="Select payment To"
                                        required={true}
                                        getOptionLabel={(option) => option.title}
                                        value={paymentToList.find((option) => option.id === values.paymentTo) || null}
                                        onChange={(e, newValue) => {
                                            const selectedPaymentTo = newValue ? newValue.id : "";
                                            if ((values.paymentTo === 1 || values.paymentTo === 2) && selectedPaymentTo === 3) {
                                                if (values.particulars && values.particulars.length > 0) {
                                                    alert("Please delete all particulars before switching to Party Ledger payment.");
                                                    return;
                                                } else {
                                                    setFieldValue('paymentTo', selectedPaymentTo);
                                                    setFieldValue('clientCompanyId', '');
                                                }
                                            }
                                            else if (values.paymentTo === 3 && (selectedPaymentTo === 1 || selectedPaymentTo === 2)) {
                                                setFieldValue('paymentTo', selectedPaymentTo);
                                                setFieldValue('partyLedgerId', '');
                                            }
                                            else {
                                                setFieldValue('paymentTo', selectedPaymentTo);
                                            }
                                        }}
                                        placeholder="Select payment To"
                                        error={touched.paymentTo && !!errors.paymentTo}
                                        helperText={touched.paymentTo && errors.paymentTo}
                                    />



                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <CustomTextField
                                        name="paymentVoucherNo"
                                        fieldLabel="Payment Voucher No"
                                        value={values.paymentVoucherNo}
                                        onChange={handleChange}
                                        error={touched.paymentVoucherNo && !!errors.paymentVoucherNo}
                                        helperText={touched.paymentVoucherNo && errors.paymentVoucherNo}
                                        placeholder="Enter Payment Voucher No"
                                        sx={CustomStyle.readOnly}
                                        readOnly={true}
                                        required={true}
                                    />
                                </Grid>
                                {/* Show Customer Name when paymentTo = 1 or 2 */}
                                {(values.paymentTo === 1 || values.paymentTo === 2) && (
                                    <Grid item xs={12} md={6}>
                                        <CustomAutocomplete
                                            options={customerList}
                                            fieldLabel="Customer Name"
                                            required={true}
                                            getOptionLabel={(option) => option.title}
                                            value={customerList.find((option) => option.id === values.clientCompanyId) || null}
                                            onChange={(e, newValue) =>
                                                setFieldValue('clientCompanyId', newValue ? newValue.id : '')
                                            }
                                            placeholder="Select Customer Name"
                                            readOnly={true}
                                            sx={CustomStyle.readOnly}
                                            error={touched.clientCompanyId && !!errors.clientCompanyId}
                                            helperText={touched.clientCompanyId && errors.clientCompanyId}
                                        />
                                    </Grid>
                                )}
                                {/* Show Party Ledger when paymentTo = 3 */}
                                {values.paymentTo === 3 && (
                                    <Grid item xs={12} md={6}>
                                        <CustomAutocomplete
                                            options={partyLedger}
                                            fieldLabel="Select Party Ledger"
                                            required={true}
                                            getOptionLabel={(option) => option.title}
                                            value={partyLedger.find((option) => option.id === values.partyLedgerId) || null}
                                            onChange={(e, newValue) =>
                                                setFieldValue('partyLedgerId', newValue ? newValue.id : '')
                                            }
                                            placeholder="Select Party Ledger"
                                            error={touched.partyLedgerId && !!errors.partyLedgerId}
                                            helperText={touched.partyLedgerId && errors.partyLedgerId}
                                        />
                                    </Grid>
                                )}
                                {/* @@@@@@@@@@@@@@@ end */}
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <CustomTextField
                                        required={true}
                                        name="date"
                                        fieldLabel="Date"
                                        type="date"
                                        value={values.date}
                                        onChange={handleChange}
                                        error={touched.date && !!errors.date}
                                        helperText={touched.date && errors.date}
                                        placeholder="Enter Date"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <CustomAutocomplete
                                        options={currencyList}
                                        fieldLabel="Currency"
                                        required={true}
                                        getOptionLabel={(option) => option.title}
                                        value={currencyList.find((option) => option.id === values.currencyId) || null}
                                        onChange={(e, newValue) =>
                                            setFieldValue('currencyId', newValue ? newValue.id : '')
                                        }
                                        placeholder="Select Currency"
                                        sx={CustomStyle.readOnly}
                                        readOnly={true}
                                        error={touched.currencyId && !!errors.currencyId}
                                        helperText={touched.currencyId && errors.currencyId}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <CustomPrefixSuffixField
                                        required={true}
                                        prefix={currencySymbol}
                                        name="amount"
                                        fieldLabel="Amount"
                                        type="number"
                                        value={values.amount}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue('amountInBaseCurrency', e.target.value * values.conversionRate || 0);
                                        }}
                                        error={touched.amount && !!errors.amount}
                                        helperText={touched.amount && errors.amount}
                                        placeholder="Enter Amount"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <CustomAutocomplete
                                        options={currencyList}
                                        fieldLabel="Base Currency"
                                        required={true}
                                        getOptionLabel={(option) => option.title}
                                        value={currencyList.find((option) => option.id === values.baseCurrencyId) || null}
                                        onChange={(e, newValue) =>
                                            setFieldValue('baseCurrencyId', newValue ? newValue.id : '')
                                        }
                                        placeholder="Select Base Currency"
                                        sx={CustomStyle.readOnly}
                                        readOnly={true}
                                        error={touched.baseCurrencyId && !!errors.baseCurrencyId}
                                        helperText={touched.baseCurrencyId && errors.baseCurrencyId}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                    <CustomPrefixSuffixField
                                        required={true}
                                        prefix={baseCurrencySymbol}
                                        tagLine="(In Base)"
                                        name="conversionRate"
                                        fieldLabel="Conversion Rate"
                                        type="number"
                                        value={values.conversionRate}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue('amountInBaseCurrency', values.amount * e.target.value || 0);
                                        }}
                                        error={touched.conversionRate && !!errors.conversionRate}
                                        helperText={touched.conversionRate && errors.conversionRate}
                                        placeholder="Enter Conversion Rate"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                    <CustomPrefixSuffixField
                                        required={true}
                                        prefix={baseCurrencySymbol}
                                        tagLine="(In Base Currency)"
                                        name="amountInBaseCurrency"
                                        fieldLabel="Amount"
                                        type="number"
                                        value={values.amountInBaseCurrency}
                                        onChange={handleChange}
                                        error={touched.amountInBaseCurrency && !!errors.amountInBaseCurrency}
                                        helperText={touched.amountInBaseCurrency && errors.amountInBaseCurrency}
                                        placeholder="Enter Amount"
                                        readOnly={true}
                                        sx={CustomStyle.readOnly}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <CustomPrefixSuffixField
                                        required={true}
                                        prefix={baseCurrencySymbol}
                                        tagLine="(In Base Currency)"
                                        name="bankCharges"
                                        fieldLabel="Bank Charges"
                                        type="number"
                                        value={values.bankCharges}
                                        onChange={handleChange}
                                        error={touched.bankCharges && !!errors.bankCharges}
                                        helperText={touched.bankCharges && errors.bankCharges}
                                        placeholder="Enter Bank Charges"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CustomTextField
                                        placeholder="Enter Reference"
                                        name="reference"
                                        fieldLabel="reference"
                                        type="text"
                                        value={values.reference}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                {(values.paymentTo === 1 || values.paymentTo === 2) && (
                                    <Grid item xs={12}>
                                        <FieldArray
                                            name="particulars"
                                            render={arrayHelpers => (
                                                <>
                                                    <Grid item xs={12}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box display="flex" flexDirection="row" alignItems="center">
                                                                <AssignmentLateOutlinedIcon />
                                                                <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                                                    {t("Particulars")}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Box style={{ overflowX: 'auto', overflowY: 'auto' }}>
                                                            <Table style={{ minWidth: '1200px', width: '100%' }}>
                                                                <TableHead>
                                                                    <TableRow style={{ height: '40px', backgroundColor: "#e7f0f7" }}>
                                                                        <TableCell style={{ ...styles.tablecell, width: "10px" }}  >{t("No.")}</TableCell>
                                                                        <TableCell style={{ ...styles.tablecell }}  >{t("Proforma Invoice")}</TableCell>
                                                                        <TableCell style={{ ...styles.tablecell }}  >{t("Commercial Invoice")}</TableCell>
                                                                        <TableCell style={{ ...styles.tablecell }}  >{t("Amount")}</TableCell>
                                                                        <TableCell style={{ ...styles.tablecell }}  >{t("Conversion Rate")}</TableCell>
                                                                        <TableCell style={{ ...styles.tablecell }}  >{t("Base Amount")}</TableCell>
                                                                        <TableCell style={{ ...styles.tablecell, width: "10px" }} rowSpan={2}>{t("Action")}</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {values.particulars.map((particular, index) => {
                                                                        return (
                                                                            <TableRow key={index}>
                                                                                <TableCell style={styles.tablecell}>{index + 1}</TableCell>

                                                                                <TableCell style={{ ...styles.tablecell, width: "200px" }}>
                                                                                    <CustomAutocomplete
                                                                                        required={true}
                                                                                        options={proformaInvoiceList}
                                                                                        getOptionLabel={(option) => option.title}
                                                                                        value={
                                                                                            proformaInvoiceList.find(
                                                                                                (option) => option.id === values.particulars[index].exportPiNo
                                                                                            ) || null
                                                                                        }
                                                                                        onChange={(e, newValue) => {
                                                                                            setFieldValue(`particulars.${index}.exportPiNo`, newValue ? newValue.id : '');
                                                                                        }}
                                                                                        placeholder="Proforma Invoice"
                                                                                        error={touched.particulars?.[index]?.exportPiNo && !!errors.particulars?.[index]?.exportPiNo}
                                                                                        helperText={touched.particulars?.[index]?.exportPiNo && errors.particulars?.[index]?.exportPiNo}
                                                                                    />

                                                                                </TableCell>
                                                                                <TableCell style={{ ...styles.tablecell, width: "200px" }}>
                                                                                    <CustomAutocomplete
                                                                                        required={true}
                                                                                        options={commercialInvoiceList}
                                                                                        getOptionLabel={(option) => option.title}
                                                                                        value={
                                                                                            commercialInvoiceList.find(
                                                                                                (option) => option.id === values.particulars[index].exportReferenceCiNo
                                                                                            ) || null
                                                                                        }
                                                                                        onChange={(e, newValue) => {
                                                                                            setFieldValue(`particulars.${index}.exportReferenceCiNo`, newValue ? newValue.id : '');
                                                                                        }}
                                                                                        placeholder="Commercial Invoice"
                                                                                        error={touched.particulars?.[index]?.exportReferenceCiNo && !!errors.particulars?.[index]?.exportReferenceCiNo}
                                                                                        helperText={touched.particulars?.[index]?.exportReferenceCiNo && errors.particulars?.[index]?.exportReferenceCiNo}
                                                                                    />
                                                                                </TableCell>

                                                                                <TableCell style={styles.tablecell}>
                                                                                    <CustomPrefixSuffixField
                                                                                        prefix={currencySymbol}
                                                                                        required={true}
                                                                                        name={`particulars.${index}.referenceAmount`}
                                                                                        type="number"
                                                                                        value={particular.referenceAmount || ''}
                                                                                        onChange={(e) => {
                                                                                            handleChange(e);
                                                                                            const refAmount = parseFloat(e.target.value) || 0;
                                                                                            const convRate = parseFloat(values.particulars[index].conversionRate) || 0;
                                                                                            setFieldValue(`particulars.${index}.amountInBaseCurrency`, refAmount * convRate);
                                                                                        }}
                                                                                        placeholder="Amount"
                                                                                        error={touched.particulars?.[index]?.referenceAmount && !!errors.particulars?.[index]?.referenceAmount}
                                                                                        helperText={touched.particulars?.[index]?.referenceAmount && errors.particulars?.[index]?.referenceAmount}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell style={styles.tablecell}>
                                                                                    <CustomPrefixSuffixField
                                                                                        prefix={baseCurrencySymbol}
                                                                                        required={true}
                                                                                        name={`particulars.${index}.conversionRate`}
                                                                                        type="number"
                                                                                        value={particular.conversionRate || ''}
                                                                                        onChange={(e) => {
                                                                                            handleChange(e);
                                                                                            const convRate = parseFloat(e.target.value) || 0;
                                                                                            const refAmount = parseFloat(values.particulars[index].referenceAmount) || 0;
                                                                                            setFieldValue(`particulars.${index}.amountInBaseCurrency`, refAmount * convRate);
                                                                                        }}
                                                                                        placeholder="Conversion Rate"
                                                                                        error={touched.particulars?.[index]?.conversionRate && !!errors.particulars?.[index]?.conversionRate}
                                                                                        helperText={touched.particulars?.[index]?.conversionRate && errors.particulars?.[index]?.conversionRate}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell style={styles.tablecell}>
                                                                                    <CustomPrefixSuffixField
                                                                                        prefix={baseCurrencySymbol}
                                                                                        required={true}
                                                                                        name={`particulars.${index}.amountInBaseCurrency`}
                                                                                        type="number"
                                                                                        value={particular.amountInBaseCurrency || ''}
                                                                                        onChange={handleChange}
                                                                                        placeholder="Base Amount"
                                                                                        error={touched.particulars?.[index]?.amountInBaseCurrency && !!errors.particulars?.[index]?.amountInBaseCurrency}
                                                                                        helperText={touched.particulars?.[index]?.amountInBaseCurrency && errors.particulars?.[index]?.amountInBaseCurrency}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell style={styles.tablecell}>
                                                                                    <DeleteOutlineOutlinedIcon
                                                                                        onClick={() => handleOpenDeleteAlert(particular.paymentVoucherParticularsId)}
                                                                                        style={{ color: '#E4003A', fontSize: "20px", cursor: "pointer" }}
                                                                                    />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>

                                                                <TableRow style={{ height: '40px' }}>
                                                                    <TableCell style={styles.tablecell} colSpan={8}>
                                                                        <div style={{ margin: "auto", textAlign: "center" }}>
                                                                            <CustomButton
                                                                                sx={Additemstyle}
                                                                                startIcon={<AddRounded />}
                                                                                title="Add Item"
                                                                                variant="contained"
                                                                                size="small"
                                                                                onClick={() => arrayHelpers.push({
                                                                                    exportReferenceCiNo: '',
                                                                                    referenceAmount: '',
                                                                                })}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>

                                                                <TableRow style={{ height: '40px' }}>
                                                                    <TableCell style={styles.tablecellfooter} colSpan={3}  > {t("Total")}</TableCell>
                                                                    <TableCell style={styles.tablecellfooter}>
                                                                        {currencySymbol}&nbsp;
                                                                        {(
                                                                            values.particulars.reduce((total, particular) => {
                                                                                const referenceAmount = parseFloat(particular.referenceAmount) || 0;
                                                                                return total + referenceAmount;
                                                                            }, 0)
                                                                        ).toFixed(2)}
                                                                    </TableCell>

                                                                    <TableCell style={styles.tablecellfooter} colSpan={3} >
                                                                        {baseCurrencySymbol}&nbsp;
                                                                        {(
                                                                            values.particulars.reduce((total, particular) => {
                                                                                const amountInBaseCurrency = parseFloat(particular.amountInBaseCurrency) || 0;
                                                                                return total + amountInBaseCurrency;
                                                                            }, 0)
                                                                        ).toFixed(2)}
                                                                    </TableCell>
                                                                </TableRow>
                                                            </Table>
                                                        </Box>
                                                    </Grid>
                                                </>
                                            )}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={8}>
                                    <CustomTextarea
                                        name="note"
                                        fieldLabel="Note"
                                        value={values.note}
                                        onChange={handleChange}
                                        placeholder="Enter Note"
                                        sx={{ height: '100px !important' }}
                                    />
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>
            <CustomDelete
                handleDelete={handleDelete}
                open={deleteAlert}
                rowId={rowId}
                handleClose={() => setDeleteAlert(false)}
            />
        </>

    );
};

export default UpdatePayment;

const styles = {
    table: {
        minWidth: 650,
    },
    textMuted: {
        color: '#6c757d',
    },

    labelCell: {
        width: '180px',
    },
    tableContainer: {
        marginBottom: '16px',
    },
    header: {
        marginBottom: '16px',
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    customTableRow: {
        height: '20px',
    },
    editButton: {
        textAlign: 'right',
    },
    showDetailsSpan: {
        padding: '8px',
        fontWeight: 600,
        fontSize: '16px',
        color: '#212529',
        cursor: 'pointer',
    },
    expandIcon: {
        color: '#212529',
    },
    boxheading: {
        width: 70,  // Width of the box
        height: 70, // Height of the box, making it square
        background: 'radial-gradient(circle, #007BFF 0, rgba(0, 134, 201, .5) 0, #007BFF 100%)', // Radial gradient background
        color: 'white', // Text color
        display: 'flex', // Center content horizontally
        alignItems: 'center', // Center content vertically
        justifyContent: 'center', // Center content horizontally
        borderRadius: 14, // Rounded corners
        boxShadow: 3, // Box shadow for depth
    },
    headingname: {
        paddingTop: '10px',
        fontWeight: '600',
        fontSize: '20px',
        color: '#212529',
    },
    avatarcircle: {
        backgroundColor: '#007BFF',
        width: '30px',
        height: '30px',
        fontSize: '15px',
    },
    cardtittile: {
        padding: '8px',
        paddingLeft: '35px',
        fontWeight: 600,
        fontSize: '16px',
        color: '#212529',
    },
    tablebutton: {
        backgroundColor: '#007bff',
        color: '#ffff',
        textTransform: 'capitalize',
        borderRadius: 4,
        border: '2px solid #007bff',
        height: 30,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    tablecell: {
        border: '1px solid #dee2e6',
        color: '#212529',
        fontWeight: 600,
        fontSize: '14px',
        textAlign: 'center',
        padding: '1px',
        paddingLeft: "10px",
        paddingRight: "10px",
        whiteSpace: "nowrap"
    },
    tablecellfooter: {
        border: '1px solid #dee2e6',
        color: '#212529',
        fontWeight: 600,
        fontSize: '14px',
        textAlign: 'right',
        padding: '1px',
        paddingLeft: "10px",
        paddingRight: "10px",
    },

};
const Additemstyle = {
    color: '#212529 !important',
    textTransform: 'capitalize !important',
    borderRadius: '4px !important',
    border: '1px dashed #aecce4 !important',
    height: '30px !important',
    display: 'inline-flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    backgroundColor: '#e7f0f7 !important',
    width: '100% !important',
};